import { startCase } from 'lodash'
import type { RExternalOrgApp } from 'v2/types/api/settings'

/**
 * The connected account's own email, when the backend knows it.
 *
 * On new-integrations-service rows the backend sets both `integrated_by.name` and
 * `integrated_by.email` to the linked account's email. The connections table then
 * overwrites `.name` with the CX user's directory name (the Owner avatar needs it),
 * so `.email` is the only field still carrying the third-party account.
 *
 * Legacy rows put the CX user who created the connection in both fields, so they
 * keep the display-name fallback — their email is not the connected account's.
 */
const getConnectedAccountEmail = (app: RExternalOrgApp): string =>
  app.integration_source === 'aci' ? app.integrated_by?.email || '' : ''

export const getAccountIdentifier = (app: RExternalOrgApp): string => {
  const { account_identifier: accountIdentifier } = app

  // Prefer the connected account's email, then whoever set the connection up. The
  // latter is all that's left for custom MCP servers and legacy rows.
  const fallbackIdentifier = () =>
    getConnectedAccountEmail(app) || startCase(app.integrated_by?.name) || ''

  // If no identifier data exists, use integrated_by
  if (!accountIdentifier || Object.keys(accountIdentifier).length === 0) {
    return fallbackIdentifier()
  }

  const keys = Object.keys(accountIdentifier)

  // If there's only one key, use its value as the identifier
  let identifier: string | null =
    keys.length === 1
      ? String(Object.values(accountIdentifier)[0] || '')
      : String(accountIdentifier.email || '') ||
        String(accountIdentifier.account_email || '') ||
        String(accountIdentifier.name || '') ||
        String(accountIdentifier.company_name || '') ||
        String(accountIdentifier.display_name || '') ||
        String(accountIdentifier.user || '') ||
        String(accountIdentifier.team || '') ||
        null

  if (!identifier || identifier === '') {
    return fallbackIdentifier()
  }

  // Remove https:// prefix if it exists
  if (typeof identifier === 'string' && identifier.startsWith('https://')) {
    identifier = identifier.replace('https://', '')
  }

  return identifier
}
